import React, { useEffect, useRef, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PricingPreview = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const content = {
    en: {
      label: 'COMING IN JUNE',
      title: 'Competitive Packages for Your Peace of Mind',
      subtitle: 'We\'re finalizing flexible pricing plans designed to fit every family\'s needs and budget. Join our waitlist to be the first to access launch pricing.',
      features: [
        'Flexible service frequencies',
        'All-inclusive pricing - no hidden fees',
        'Photo documentation included',
        'Special rates for founding members',
        'Cancel or adjust anytime'
      ],
      cta: 'Join Waitlist for Early Access'
    },
    fr: {
      label: 'LANCEMENT EN JUIN',
      title: 'Forfaits Compétitifs pour Votre Tranquillité d\'Esprit',
      subtitle: 'Nous finalisons des plans tarifaires flexibles conçus pour répondre aux besoins et au budget de chaque famille. Rejoignez notre liste d\'attente pour accéder en priorité aux tarifs de lancement.',
      features: [
        'Fréquences de service flexibles',
        'Tarifs tout compris - pas de frais cachés',
        'Documentation photo incluse',
        'Tarifs spéciaux pour les membres fondateurs',
        'Annulez ou ajustez à tout moment'
      ],
      cta: 'Rejoindre la Liste d\'Attente'
    }
  };

  const t = content[lang];

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={sectionRef} id="pricing" className="py-24 md:py-32 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-indigo-200 mb-6">
            <Sparkles className="w-4 h-4 text-[#5B4FE9]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
              {t.label}
            </span>
          </div>
          
          <h2
            className="text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Features Grid */}
        <div
          className={`bg-white rounded-2xl border border-gray-100 shadow-lg p-8 md:p-10 transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="space-y-4 mb-8">
            {t.features.map((feature, i) => (
              <div
                key={i}
                className="flex items-start gap-3"
              >
                <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="text-gray-700 leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => handleNavClick('#get-started')}
            className="w-full py-4 bg-[#5B4FE9] text-white font-semibold rounded-full hover:bg-[#4A3ED8] transition-colors duration-200 shadow-lg shadow-indigo-200/50"
          >
            {t.cta}
          </button>

          <p className="text-center text-xs text-gray-500 mt-4">
            {lang === 'fr' 
              ? 'Les membres de la liste d\'attente recevront un accès prioritaire aux tarifs de lancement.'
              : 'Waitlist members will receive priority access to launch pricing.'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingPreview;
